import { Authenticator, Image } from "@aws-amplify/ui-react";

import "@aws-amplify/ui-react/styles.css";

import { useEffect, useReducer, useState } from "react";

import "./styles/index.css";

import { ChatAssistantAttachmentContextProvider } from "@/contexts/ChatAssistantAttachmentContext";
import EnableMfaTotp from "@/EnableMfaTotp";
import { USER_ROLE_ADMIN, USER_ROLE_PRODUCER } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { Experimental_CssVarsProvider as CssVarsProvider } from "@mui/material/styles";
import { GROUP_TILLEY_BROKERS, GROUP_TILLEY_LENDERS } from "api/AmplifySessionManager";
import { fetchAuthSession, fetchMFAPreference, signOut } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import NavigationRoutes from "components/Menu/components/NavigationRoutes";
import { getStorageValue, setStorageValue } from "core/useLocalStorage";
import { useSession } from "core/useSession";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";
import { IconContext } from "react-icons";

import AppContext from "./core/AppContext";
import { NO_MFA, STORAGE_VALUE_MFA } from "./core/AuthHelper";
import { theme } from "./theme/theme";

export { default as AppContext } from "./core/AppContext";

window._ = _;

/// Reducer for AppContext state.
const reducer = (state, action) => {
  // console.log("globalDispatch", state, action);

  const LOCALSTORAGE_KEYS = [
    "crop_year",
    "subscription_payment_timestamp",
    "search_text_portfolio",
    "filter_agent_token", //array
    "isMenuOpen",
  ];

  switch (action.type) {
    case "update": {
      let nextValue = action.payload.value;
      // filter_agent_token is stored as a comma-joined string; duplicates grow into N identical Broker chips
      if (action.payload.key === "filter_agent_token" && Array.isArray(nextValue)) {
        const seen = new Set();
        nextValue = nextValue
          .map((t) => String(t ?? "").trim().toLowerCase())
          .filter((t) => {
            if (!t || seen.has(t)) return false;
            seen.add(t);
            return true;
          });
      }
      if (LOCALSTORAGE_KEYS.includes(action.payload.key)) {
        setStorageValue(action.payload.key, nextValue);
      }

      return {
        ...state,
        [action.payload.key]: nextValue,
      };
    }

    case "updateMyFarms":
      // console.log("globalDispatch / updateMyFarms", action.payload.value);
      return {
        ...state,
        myFarms: action.payload.value || [],
        myFarmsTimestamp: new Date(),
      };

    case "updateFarm": {
      const _selectedFarm = action.payload.value;
      const __myFarms = state.myFarms.map((ff) => {
        if (ff.uid === _selectedFarm.uid) {
          return _selectedFarm;
        } else {
          return ff;
        }
      });

      return {
        ...state,
        myFarms: __myFarms || [],
        myFarmsTimestamp: new Date(),
      };
    }

    case "updateFarms": {
      const _farmsToUpdate = action.payload.value;
      const ___myFarms = state.myFarms.map((ff) => {
        const _findFarm = _farmsToUpdate.find((f) => f.uid === ff.uid);
        return _findFarm || ff;
      });

      return {
        ...state,
        myFarms: ___myFarms || [],
        myFarmsTimestamp: new Date(),
      };
    }

    case "updateMyFarmsInsurance":
      return {
        ...state,
        myFarmsInsurance: action.payload.value,
      };

    case "setProducer":
      setStorageValue("producer_token", action.payload.producer_token);

      return {
        ...state,
        producer_token: action?.payload?.producer_token,
        agent_token: action?.payload?.agent_token,
      };

    case "login":
      setStorageValue("user_role", action.payload.user_role);
      setStorageValue("producer_token", action.payload.producer_token);
      setStorageValue("agent_token", action.payload.agent_token);

      setStorageValue("user_groups", action.payload.user_groups);
      setStorageValue("is_lender", action.payload.is_lender);
      setStorageValue("is_broker", action.payload.is_broker);
      setStorageValue("is_admin", action.payload.is_admin);

      // setStorageValue("bearer_token", action.payload.bearer_token);
      // setStorageValue("refresh_token", action.payload.refresh_token);

      return {
        ...state,
        user_role: action.payload.user_role,
        user_groups: action.payload.user_groups,
        is_lender: action.payload.is_lender || false,
        is_broker: action.payload.is_broker || false,
        is_admin: action.payload.is_admin || false,

        producer_token: action.payload.producer_token,
        agent_token: action.payload.agent_token,

        isDatanacAuthenticated: true,

        bearer_token: action.payload.bearer_token,
        refresh_token: action.payload.refresh_token,
      };

    case "logout":
      setStorageValue("producer_token", null);
      setStorageValue("agent_token", null);
      setStorageValue("mfaType", null);

      // setStorageValue("bearer_token", null);
      // setStorageValue("refresh_token", null);

      signOut();

      return {
        ...state,
        user_role: null,
        producer_token: null,
        agent_token: null,

        bearer_token: null,
        refresh_token: null,

        isDatanacAuthenticated: false,

        user_groups: getStorageValue("user_groups", []) || [],
        is_lender: false,
        is_broker: false,
        is_admin: false,
      };

    case "resetAuthentication":
      //setStorageValue("bearer_token", null);
      //setStorageValue("refresh_token", null);

      return {
        ...state,
        bearer_token: null,
        refresh_token: null,
      };

    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
};

const initializer = () => {
  const _filter_agent_token = getStorageValue("filter_agent_token", null);
  // Unpack array; unique+lowercase so stale localStorage dups don't recreate N Broker chips
  let _filter_agent_token_array = [];
  if (_filter_agent_token) {
    const seen = new Set();
    for (const t of String(_filter_agent_token).split(",")) {
      const k = t.trim().toLowerCase();
      if (!k || seen.has(k)) continue;
      seen.add(k);
      _filter_agent_token_array.push(k);
    }
  }

  const _state = {
    user_role: getStorageValue("user_role"),

    producer_token: getStorageValue("producer_token"),
    agent_token: getStorageValue("agent_token"),

    user_groups: getStorageValue("user_groups", []) || [],
    is_lender: getStorageValue("is_lender", false),
    is_broker: getStorageValue("is_broker", false),
    is_admin: getStorageValue("is_admin", false),

    bearer_token: "", //getStorageValue("bearer_token"),
    refresh_token: "", //getStorageValue("refresh_token"),

    // Filters:
    isMenuOpen: getStorageValue("isMenuOpen", true),
    crop_year: getStorageValue("crop_year") || getCurrentReinsuranceYear(),
    search_text_portfolio: getStorageValue("search_text_portfolio", "") || "",
    filter_agent_token: _filter_agent_token_array || [],

    subscription_payment_timestamp: getStorageValue("subscription_payment_timestamp"),

    myFarms: [],
    myFarmsTimestamp: "",
    myFarmsInsurance: [],
  };

  return _state;
};

// export const neutralizeBack = () => {
//   window.history.pushState(null, "", window.location.href);
//   window.onpopstate = () => {
//     window.history.pushState(null, "", window.location.href);
//   };
// };
// neutralizeBack();

function App() {
  const [globalState, globalDispatch] = useReducer(reducer, {}, initializer);
  const session = useSession({ globalState });
  const [mfaTypeState, setMfaTypeState] = useState(getStorageValue(STORAGE_VALUE_MFA) || NO_MFA);

  useEffect(() => {
    const loadMyUser = async () => {
      try {
        checkMfaStatus();
        const { tokens } = await fetchAuthSession();
        const idToken = tokens?.idToken;

        const user_role = idToken?.payload["custom:datanac_user_role"];

        const producer_token =
          user_role === USER_ROLE_PRODUCER
            ? idToken?.payload["custom:producer_token"]
            : getStorageValue("producer_token")?.toUpperCase(); // Normalize to upper
        const agent_token = (
          idToken?.payload["custom:agent_token"]
            ? idToken?.payload["custom:agent_token"]
            : getStorageValue("agent_token")
        )?.toUpperCase(); // Normalize to upper

        const decodedToken = jwtDecode(idToken.toString());
        const _user_groups = decodedToken["cognito:groups"] || [];
        const _is_lender = _user_groups?.includes(GROUP_TILLEY_LENDERS) || false;
        const _is_broker = _user_groups?.includes(GROUP_TILLEY_BROKERS) || false;
        const _is_admin = user_role === USER_ROLE_ADMIN || false;

        globalDispatch({
          type: "login",
          payload: {
            producer_token: producer_token,
            agent_token: agent_token,
            user_role: user_role,

            user_groups: _user_groups,
            is_admin: Boolean(_is_admin),
            is_lender: Boolean(_is_lender),
            is_broker: Boolean(_is_broker),
          },
        });
      } catch (error) {
        console.error("Error loading user:", error);
      }
    };
    Hub.listen("auth", loadMyUser);
  }, []);

  // useEffect(() => {
  //   if (globalState.myFarmsTimestamp) {
  //     // console.log("updateUserFarmApi", state.myFarmsTimestamp, state.myFarms);
  //     updateUserFarmApi({
  //       user_role: globalState.user_role,
  //       agent_token: globalState.agent_token,
  //       producer_token: globalState.producer_token,
  //       my_farms: globalState.myFarms
  //     });
  //   }
  // }, [globalState.myFarms, globalState.myFarmsTimestamp])

  const checkMfaStatus = async () => {
    try {
      const { preferred: mfaType } = await fetchMFAPreference();
      setStorageValue(STORAGE_VALUE_MFA, mfaType || NO_MFA);
      setMfaTypeState(mfaType || NO_MFA);
    } catch (error) {
      console.error("Error checking MFA status:", error);
    }
  };

  const handleLogoutClick = () => {
    globalDispatch({ type: "logout" });
  };
  return (
    <ChatAssistantAttachmentContextProvider>
      <Authenticator
        hideSignUp={true}
        formFields={{
          signIn: {
            username: {
              placeholder: "Typically your email address",
            },
          },
        }}
        components={{
          Header() {
            return (
              <Box
                sx={{
                  textAlign: "center",
                  pb: 6,
                  pt: 21,
                  px: 15,
                }}
              >
                <Image alt="Tilley" src="/images/Tilley_logo.svg" />
              </Box>
            );
          },

          ConfirmSignIn: {
            Header() {
              return (
                <Box sx={{ textAlign: "left", mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: "34px",
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Enter Your 2FA Code
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      padding: "0.75rem 0.875rem",
                      backgroundColor: "var(--mui-palette-colors-backgroundSuccess)",
                      borderRadius: "0.5rem",
                      marginBottom: "1.25rem",
                    }}
                  >
                    Please enter the 6-digit code from your authenticator app (like Google Authenticator or Authy). This
                    code refreshes every 30 second
                  </Typography>
                </Box>
              );
            },
          },

          Footer() {
            return (
              <Stack sx={{ marginTop: "auto", alignItems: "center", paddingBottom: "2.5rem" }}>
                <Divider flexItem />
                <Typography sx={{ fontWeight: 600 }}>
                  Customer Support: <a href="tel:+1-800-823-5572">800-823-5572</a>
                </Typography>
                <Typography sx={{ marginTop: "1rem", fontWeight: 600 }}>
                  © {new Date().getFullYear()} Tilley by Daitaas
                </Typography>
              </Stack>
            );
          },

          ForgotPassword: {
            Footer() {
              return (
                <Box sx={{ fontWeight: 600, textAlign: "left", paddingTop: "1.5rem" }}>
                  <Typography sx={{ fontWeight: 600 }} color="textSecondary">
                    Password Requirements:
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      paddingLeft: "1.5rem",
                    }}
                  >
                    <li>At least 8 characters</li>
                    <li>One uppercase and one lowercase letter</li>
                    <li>One number</li>
                    <li>One special character (e.g., @, $, etc.)</li>
                  </Box>
                </Box>
              );
            },
          },
        }}
      >
        {getStorageValue("user_role") !== USER_ROLE_PRODUCER && mfaTypeState === NO_MFA ? (
          <EnableMfaTotp handleLogoutClick={handleLogoutClick} type={"login"} onClose={checkMfaStatus} />
        ) : (
          <AppContext.Provider value={{ globalState: globalState, globalDispatch: globalDispatch, session: session }}>
            <CssVarsProvider theme={theme}>
              <IconContext.Provider value={{ className: "react-icons", size: "auto" }}>
                <NavigationRoutes />
              </IconContext.Provider>
            </CssVarsProvider>
          </AppContext.Provider>
        )}
      </Authenticator>
    </ChatAssistantAttachmentContextProvider>
  );
}

export default App;
